import { useState, useEffect } from "react";
import { createPortal } from "react-dom";
import DaumPostcode from "react-daum-postcode";
import "./css/storeinfomodal.css";
import {
  modStoreInfo,
  modSubsInfo,
  modMemberInfo,
  modBizInfo,
  modAuthInfo,
  modMemberPassword,
  modManagerPassword,
  deleteManager,
  approveStore,
  suspendStore,
  unsuspendStore,
  getStoreDetail,
} from "../../../utils/adminStoreService";
import {
  getBankList,
  getMembershipList,
  getAuthList,
  formatDateTime24,
} from "../../../utils/commonService";
import { getPasswordCheckMessage } from "../../../utils/registerValidation";

const StoreInfoModal = ({
  storeCode,
  onClose,
  isWithdrawal = false,
  withdrawalRequestDateTime = null,
}) => {
  // 가맹점 상세 데이터
  const [storeData, setStoreData] = useState(null);

  // 은행 및 멤버십 목록
  const [bankList, setBankList] = useState([]);
  const [membershipList, setMembershipList] = useState([]);
  const [authList, setAuthList] = useState([]);
  const [selectedAuthIdx, setSelectedAuthIdx] = useState(null);

  // 영업일 옵션
  const openDayOptions = [
    { value: "월", label: "월" },
    { value: "화", label: "화" },
    { value: "수", label: "수" },
    { value: "목", label: "목" },
    { value: "금", label: "금" },
    { value: "토", label: "토" },
    { value: "일", label: "일" },
  ];

  // 한글 요일을 enum으로 변환
  const convertDayToEnum = (day) => {
    const dayMap = {
      월: "MON",
      화: "TUE",
      수: "WED",
      목: "THU",
      금: "FRI",
      토: "SAT",
      일: "SUN",
    };
    return dayMap[day] || day;
  };

  // enum을 한글 요일로 변환
  const convertEnumToDay = (enumDay) => {
    const dayMap = {
      MON: "월",
      TUE: "화",
      WED: "수",
      THU: "목",
      FRI: "금",
      SAT: "토",
      SUN: "일",
    };
    return dayMap[enumDay] || enumDay;
  };

  // 각 섹션별 수정 모드 상태
  const [editModes, setEditModes] = useState({
    store: false,
    subscription: false,
    member: false,
    business: false,
    auth: false,
  });

  // 매니저 비밀번호 변경 상태
  const [managerPasswordModal, setManagerPasswordModal] = useState({
    isOpen: false,
    memberId: null,
    memberNm: "",
    newPassword: "",
    confirmPassword: "",
  });

  // 주소 검색 모달 상태
  const [addressSearchOpen, setAddressSearchOpen] = useState(false);

  // 주소 검색 완료 핸들러
  const handleAddressComplete = (data) => {
    const fullAddress = data.jibunAddress || data.autoJibunAddress;
    setStoreForm((prev) => ({
      ...prev,
      storeAddr: fullAddress,
      zipCode: data.zonecode,
      storeAddrDetail: "",
    }));
    setAddressSearchOpen(false);
  };

  // 이미지 뷰어 상태
  const [imageViewer, setImageViewer] = useState({
    isOpen: false,
    imageUrl: "",
    imageName: "",
  });

  // 선택된 이미지/파일 상태
  const [selectedImage, setSelectedImage] = useState({
    url: "",
    name: "",
  });

  // 파일 타입 확인 함수
  const getFileType = (fileName) => {
    const extension = fileName.toLowerCase().split(".").pop();
    if (["pdf"].includes(extension)) return "pdf";
    if (["jpg", "jpeg", "png", "gif", "webp"].includes(extension))
      return "image";
    return "other";
  };

  // 파일 클릭 핸들러
  const handleFileClick = (file) => {
    setSelectedImage({ url: file.fileUrl, name: file.originalName });
  };

  // 은행 및 멤버십 목록 조회 및 가맹점 상세 정보 조회
  useEffect(() => {
    const fetchData = async () => {
      try {
        // 가맹점 상세 정보 조회
        const storeResult = await getStoreDetail(storeCode);

        if (storeResult.success) {
          setStoreData(storeResult.data);
        } else {
          console.error("가맹점 상세 정보 조회 실패:", storeResult.error);
        }

        // 은행 목록 조회
        const bankResult = await getBankList();
        if (bankResult.success) {
          setBankList(bankResult.data);
        }

        // 멤버십 목록 조회
        const membershipResult = await getMembershipList();
        if (membershipResult.success) {
          setMembershipList(membershipResult.data);
        }

        // 권한 목록 조회
        const authResult = await getAuthList();
        if (authResult.success) {
          setAuthList(authResult.data);
        }
      } catch (error) {}
    };

    if (storeCode) {
      fetchData();
    }
  }, [storeCode]);

  // storeData가 변경될 때 폼 데이터 업데이트
  useEffect(() => {
    // bankNm으로부터 bankIdx 찾기
    const selectedBank = bankList.find(
      (bank) => bank.bankNm === storeData?.bankNm,
    );

    // storeOpenDate가 enum 배열이면 한글로 변환
    const openDaysKorean = Array.isArray(storeData?.storeOpenDate)
      ? storeData.storeOpenDate.map((day) => convertEnumToDay(day))
      : [];

    setStoreForm({
      storeNm: storeData?.storeNm || "",
      storeAddr: storeData?.storeAddr || "",
      storeAddrDetail: storeData?.storeAddrDetail || "",
      zipCode: storeData?.zipCode || "",
      bankIdx: selectedBank?.bankIdx || "",
      storeAccNo: storeData?.storeAccNo || "",
      storeTelNo: storeData?.storeTelNo || "",
      storeOpenDate: openDaysKorean,
      storeOpenTime: storeData?.storeOpenTime || "",
      storeCloseTime: storeData?.storeCloseTime || "",
      storeNote: storeData?.storeNote || "",
      approvalStatus: storeData?.approvalStatus || "",
    });

    // membershipPlanIdx를 찾기 위해 membershipList에서 현재 선택된 플랜 검색
    const selectedPlan = membershipList.find(
      (plan) => plan.planNm === storeData?.membershipNm,
    );

    setSubscriptionForm({
      membershipPlanIdx: selectedPlan?.planIdx
        ? selectedPlan.planIdx.toString()
        : "",
      isRenew: storeData?.isRenew || false,
      nextBillingDate: storeData?.nextBillingDate
        ? new Date(storeData.nextBillingDate).toISOString().split("T")[0]
        : "",
    });

    setMemberForm({
      memberLoginId: storeData?.memberLoginId || "",
      ownerNm: storeData?.ownerNm || "",
      ownerTelNo: storeData?.ownerTelNo || "",
      newPassword: "",
    });

    setBusinessForm({
      bizNm: storeData?.bizNm || "",
      bizType: storeData?.bizType || "",
      bizRegDate: storeData?.bizRegDate || "",
      bizNo: storeData?.bizNo || "",
      ownerNm: storeData?.ownerNm || "",
      ownerTelNo: storeData?.ownerTelNo || "",
      storeNote: storeData?.storeNote || "",
    });

    setSelectedMenus(
      Array.isArray(storeData?.menuAuth)
        ? storeData.menuAuth.map((m) => m.code)
        : [],
    );
  }, [storeData, bankList, membershipList]);

  // 각 섹션별 폼 데이터
  const [storeForm, setStoreForm] = useState({
    storeNm: "",
    storeAddr: "",
    storeAddrDetail: "",
    zipCode: "",
    bankIdx: "",
    storeAccNo: "",
    storeTelNo: "",
    storeOpenDate: [],
    storeOpenTime: "",
    storeCloseTime: "",
    storeNote: "",
    approvalStatus: "",
  });

  const [subscriptionForm, setSubscriptionForm] = useState({
    membershipPlanIdx: "",
    isRenew: false,
    nextBillingDate: "",
  });

  // 선택된 플랜 가져오기
  const getSelectedPlan = () => {
    const plan = membershipList.find(
      (p) => p.planIdx === parseInt(subscriptionForm.membershipPlanIdx),
    );
    return plan || { planNm: "", planPrice: 0 };
  };

  // 선택된 권한 가져오기
  const getSelectedAuth = () => {
    const auth = authList.find((a) => a.authIdx === parseInt(selectedAuthIdx));
    return auth || null;
  };

  const [memberForm, setMemberForm] = useState({
    memberLoginId: "",
    ownerTelNo: "",
    newPassword: "",
  });

  const [businessForm, setBusinessForm] = useState({
    bizNm: "",
    bizType: "",
    bizRegDate: "",
    bizNo: "",
    ownerNm: "",
    ownerTelNo: "",
    storeNote: "",
  });

  // 권한 관련 상태 - menuAuth를 배열로 변환
  const [selectedMenus, setSelectedMenus] = useState(
    Array.isArray(storeData?.menuAuth)
      ? storeData.menuAuth.map((m) => m.code)
      : [],
  );

  // 메뉴 토글
  const handleMenuToggle = (code) => {
    setSelectedMenus((prev) => {
      if (prev.includes(code)) {
        return prev.filter((id) => id !== code);
      } else {
        return [...prev, code];
      }
    });
  };

  // 권한명 변경 시 해당 권한의 menuAuth로 selectedMenus 업데이트
  const handleAuthChange = (authIdx) => {
    const selectedAuth = authList.find((a) => a.authIdx === parseInt(authIdx));
    if (selectedAuth) {
      setSelectedAuthIdx(authIdx);
      // menuAuth에서 code만 추출
      const menuCodes = selectedAuth.menuAuth.map((m) => m.code);
      setSelectedMenus(menuCodes);
    }
  };

  // 현재 선택된 권한의 메뉴 목록
  const getAvailableMenus = () => {
    const selectedAuth = getSelectedAuth();
    if (selectedAuth) {
      return selectedAuth.menuAuth;
    }
    return storeData?.menuAuth || [];
  };

  // 섹션별 수정 모드 토글
  const toggleEditMode = (section) => {
    setEditModes((prev) => ({
      ...prev,
      [section]: !prev[section],
    }));
  };

  // 섹션별 취소
  const handleCancel = (section) => {
    if (section === "store") {
      const selectedBank = bankList.find(
        (bank) => bank.bankNm === storeData?.bankNm,
      );
      const openDaysKorean = Array.isArray(storeData?.storeOpenDate)
        ? storeData.storeOpenDate.map((day) => convertEnumToDay(day))
        : [];
      setStoreForm({
        storeNm: storeData?.storeNm || "",
        storeAddr: storeData?.storeAddr || "",
        storeAddrDetail: storeData?.storeAddrDetail || "",
        zipCode: storeData?.zipCode || "",
        bankIdx: selectedBank?.bankIdx || "",
        storeAccNo: storeData?.storeAccNo || "",
        storeTelNo: storeData?.storeTelNo || "",
        storeOpenDate: openDaysKorean,
        storeOpenTime: storeData?.storeOpenTime || "",
        storeCloseTime: storeData?.storeCloseTime || "",
        approvalStatus: storeData?.approvalStatus || "",
      });
    } else if (section === "subscription") {
      const selectedPlan = membershipList.find(
        (plan) => plan.planNm === storeData?.membershipNm,
      );
      setSubscriptionForm({
        membershipPlanIdx: selectedPlan?.planIdx
          ? selectedPlan.planIdx.toString()
          : "",
        isRenew: storeData?.isRenew || false,
        nextBillingDate: storeData?.nextBillingDate
          ? new Date(storeData.nextBillingDate).toISOString().split("T")[0]
          : "",
      });
    } else if (section === "member") {
      setMemberForm({
        memberLoginId: storeData?.memberLoginId || "",
        ownerTelNo: storeData?.ownerTelNo || "",
        newPassword: "",
      });
    } else if (section === "business") {
      setBusinessForm({
        bizNm: storeData?.bizNm || "",
        bizType: storeData?.bizType || "",
        bizRegDate: storeData?.bizRegDate || "",
        bizNo: storeData?.bizNo || "",
        ownerNm: storeData?.ownerNm || "",
        ownerTelNo: storeData?.ownerTelNo || "",
        storeNote: storeData?.storeNote || "",
      });
    } else if (section === "auth") {
      setSelectedMenus(
        Array.isArray(storeData?.menuAuth)
          ? storeData.menuAuth.map((m) => m.code)
          : [],
      );
    }
    toggleEditMode(section);
  };

  // 가맹점 정보 저장
  const handleSaveStoreInfo = async () => {
    try {
      const currentStatus = storeData?.approvalStatus;
      const newStatus = storeForm.approvalStatus;

      console.log(
        "[StoreInfo] currentStatus:",
        currentStatus,
        "/ newStatus:",
        newStatus,
        "/ storeCode:",
        storeData?.storeCode,
      );

      if (newStatus && newStatus !== currentStatus) {
        console.log(
          "[StoreInfo] 상태 변경 감지 →",
          newStatus === "제재" ? "suspend" : "unsuspend",
          "API 호출",
        );
        const statusResult =
          newStatus === "제재"
            ? await suspendStore(storeData.storeCode)
            : await unsuspendStore(storeData.storeCode);

        console.log("[StoreInfo] 상태 API 결과:", statusResult);

        if (!statusResult.success) {
          alert(statusResult.error);
          return;
        }
      }

      const formData = new FormData();
      formData.append("storeNm", storeForm.storeNm);
      formData.append("storeAddr", storeForm.storeAddr);
      formData.append("storeAddrDetail", storeForm.storeAddrDetail);
      formData.append("zipCode", storeForm.zipCode);
      formData.append("bankIdx", storeForm.bankIdx);
      formData.append("storeAccNo", storeForm.storeAccNo);
      formData.append("storeTelNo", storeForm.storeTelNo);
      formData.append("storeOpenTime", storeForm.storeOpenTime);
      formData.append("storeCloseTime", storeForm.storeCloseTime);
      formData.append("storeNote", storeForm.storeNote);

      storeForm.storeOpenDate.forEach((day) => {
        formData.append("storeOpenDate", convertDayToEnum(day));
      });

      const result = await modStoreInfo(storeData.storeCode, formData);
      if (result.success) {
        alert("가맹점 정보가 수정되었습니다.");
        toggleEditMode("store");
        const refreshResult = await getStoreDetail(storeCode);
        if (refreshResult.success) {
          setStoreData(refreshResult.data);
        }
      } else {
        alert(result.error);
      }
    } catch (e) {
      console.error("[StoreInfo] handleSaveStoreInfo 오류:", e);
      alert("저장 중 오류가 발생했습니다: " + e.message);
    }
  };

  // 구독 정보 저장
  const handleSaveSubscriptionInfo = async () => {
    // membershipPlanIdx를 Long으로 변환하고 검증
    if (!subscriptionForm.membershipPlanIdx) {
      alert("플랜을 선택해주세요.");
      return;
    }

    const subsData = {
      membershipPlanIdx: parseInt(subscriptionForm.membershipPlanIdx), // Long으로 변환
      isRenew:
        subscriptionForm.isRenew === true ||
        subscriptionForm.isRenew === "true",
    };

    const result = await modSubsInfo(storeData.storeCode, subsData);
    if (result.success) {
      alert("구독 정보가 수정되었습니다.");
      toggleEditMode("subscription");
      // 데이터 재로드
      const refreshResult = await getStoreDetail(storeCode);
      if (refreshResult.success) {
        setStoreData(refreshResult.data);
      }
    } else {
      alert(result.error);
    }
  };

  // 사용자 정보 저장
  const handleSaveMemberInfo = async () => {
    const result = await modMemberInfo(storeData.storeCode, {
      memberLoginId: memberForm.memberLoginId,
      ownerNm: memberForm.ownerNm,
      ownerTelNo: memberForm.ownerTelNo,
    });
    if (result.success) {
      alert("사용자 정보가 수정되었습니다.");

      // 비밀번호가 입력된 경우 비밀번호도 변경
      if (memberForm.newPassword) {
        const pwResult = await modMemberPassword(storeData.storeCode, {
          newPassword: memberForm.newPassword,
        });
        if (pwResult.success) {
          alert("비밀번호도 변경되었습니다.");
        } else {
          alert("비밀번호 변경 실패: " + pwResult.error);
        }
      }

      toggleEditMode("member");
      // 데이터 재로드
      const refreshResult = await getStoreDetail(storeCode);
      if (refreshResult.success) {
        setStoreData(refreshResult.data);
      }
    } else {
      alert(result.error);
    }
  };

  // 사업자 정보 저장
  const handleSaveBusinessInfo = async () => {
    const result = await modBizInfo(storeData.storeCode, businessForm);
    if (result.success) {
      alert("사업자 정보가 수정되었습니다.");
      toggleEditMode("business");
      // 데이터 재로드
      const refreshResult = await getStoreDetail(storeCode);
      if (refreshResult.success) {
        setStoreData(refreshResult.data);
      }
    } else {
      alert(result.error);
    }
  };

  // 권한 정보 저장
  const handleSaveAuthInfo = async () => {
    // 메뉴 배열을 JSON 배열 형식으로 전송
    const result = await modAuthInfo(storeData.storeCode, {
      menuAuth: selectedMenus,
    });
    if (result.success) {
      alert("권한 정보가 수정되었습니다.");
      toggleEditMode("auth");
      // 데이터 재로드
      const refreshResult = await getStoreDetail(storeCode);
      if (refreshResult.success) {
        setStoreData(refreshResult.data);
      }
    } else {
      alert(result.error);
    }
  };

  // 매니저 비밀번호 변경 모달 열기
  const handleManagerPasswordChange = (memberId, memberNm) => {
    setManagerPasswordModal({
      isOpen: true,
      memberId,
      memberNm,
      newPassword: "",
      confirmPassword: "",
    });
  };

  // 매니저 비밀번호 변경 저장
  const handleManagerPasswordSave = async () => {
    if (!managerPasswordModal.newPassword.trim()) {
      alert("새 비밀번호를 입력해주세요.");
      return;
    }

    if (
      managerPasswordModal.newPassword !== managerPasswordModal.confirmPassword
    ) {
      alert("비밀번호가 일치하지 않습니다.");
      return;
    }

    // 비밀번호 검증 (registerValidation 사용)
    const validation = getPasswordCheckMessage(
      managerPasswordModal.newPassword,
    );
    if (validation.status !== "success") {
      alert(validation.message);
      return;
    }

    try {
      const result = await modManagerPassword(managerPasswordModal.memberId, {
        newPassword: managerPasswordModal.newPassword,
      });

      if (result.success) {
        alert(
          `${managerPasswordModal.memberNm} 매니저의 비밀번호가 변경되었습니다.`,
        );
        setManagerPasswordModal({
          isOpen: false,
          memberId: null,
          memberNm: "",
          newPassword: "",
          confirmPassword: "",
        });
      } else {
        alert(result.error || "비밀번호 변경에 실패했습니다.");
      }
    } catch (error) {
      alert("네트워크 오류가 발생했습니다.");
      console.error("비밀번호 변경 오류:", error);
    }
  };

  // 매니저 삭제
  const handleManagerDelete = async (memberId, memberNm) => {
    if (!window.confirm(`${memberNm} 매니저를 삭제하시겠습니까?`)) {
      return;
    }

    try {
      const result = await deleteManager(memberId);

      if (result.success) {
        alert(`${memberNm} 매니저가 삭제되었습니다.`);
        // 가맹점 상세 정보 다시 조회하여 매니저 목록 갱신
        const storeResult = await getStoreDetail(storeCode);
        if (storeResult.success) {
          setStoreData(storeResult.data);
        }
      } else {
        alert(result.error || "매니저 삭제에 실패했습니다.");
      }
    } catch (error) {
      alert("네트워크 오류가 발생했습니다.");
      console.error("매니저 삭제 오류:", error);
    }
  };

  // 매니저 비밀번호 변경 모달 닫기
  const handleManagerPasswordCancel = () => {
    setManagerPasswordModal({
      isOpen: false,
      memberId: null,
      memberNm: "",
      newPassword: "",
      confirmPassword: "",
    });
  };

  return (
    <div
      className={`storeinfomodal-backdrop ${selectedImage.name?.toLowerCase().endsWith(".pdf") ? "storeinfomodal-backdrop-pdf" : ""}`}
    >
      <div
        className={`storeinfomodal-wrap ${selectedImage.name?.toLowerCase().endsWith(".pdf") ? "storeinfomodal-wrap-pdf" : ""}`}
        onClick={(e) => e.stopPropagation()}
      >
        {/* 헤더 */}
        <div className="storeinfomodal-header">
          <h2 className="storeinfomodal-title">가맹점 상세 정보</h2>
          <div className="storeinfomodal-header-actions">
            <button className="storeinfomodal-close-btn" onClick={onClose}>
              ✕
            </button>
          </div>
        </div>

        <div className="storeinfomodal-content">
          {isWithdrawal && (
            <section className="storeinfomodal-section storeinfomodal-withdrawal-section">
              <h3 className="storeinfomodal-section-title storeinfomodal-withdrawal-title">
                탈퇴 정보
              </h3>
              <div className="storeinfomodal-grid">
                <div className="storeinfomodal-field">
                  <label className="storeinfomodal-label">탈퇴 요청일</label>
                  <span className="storeinfomodal-value">
                    {formatDateTime24(withdrawalRequestDateTime) || "-"}
                  </span>
                </div>
              </div>
            </section>
          )}

          {/* 1. 가맹점 정보 섹션 */}
          <section className="storeinfomodal-section">
            <div className="storeinfomodal-section-header">
              <h3 className="storeinfomodal-section-title">가맹점 정보</h3>
              {!isWithdrawal &&
                (editModes.store ? (
                  <div className="storeinfomodal-section-actions">
                    <button
                      className="storeinfomodal-btn storeinfomodal-save-btn"
                      onClick={handleSaveStoreInfo}
                    >
                      저장
                    </button>
                    <button
                      className="storeinfomodal-btn storeinfomodal-cancel-btn"
                      onClick={() => handleCancel("store")}
                    >
                      취소
                    </button>
                  </div>
                ) : (
                  <button
                    className="storeinfomodal-btn storeinfomodal-edit-btn"
                    onClick={() => toggleEditMode("store")}
                  >
                    수정
                  </button>
                ))}
            </div>
            <div className="storeinfomodal-grid">
              <div className="storeinfomodal-field">
                <label className="storeinfomodal-label">가맹점 이름</label>
                {editModes.store ? (
                  <input
                    type="text"
                    value={storeForm.storeNm}
                    onChange={(e) =>
                      setStoreForm({ ...storeForm, storeNm: e.target.value })
                    }
                    className="storeinfomodal-input"
                  />
                ) : (
                  <span className="storeinfomodal-value">
                    {storeData?.storeNm}
                  </span>
                )}
              </div>

              <div className="storeinfomodal-field">
                <label className="storeinfomodal-label">가맹점 코드</label>
                <span className="storeinfomodal-value">
                  {storeData?.storeCode}
                </span>
              </div>

              <div className="storeinfomodal-field storeinfomodal-field-full">
                <label className="storeinfomodal-label">주소</label>
                {editModes.store ? (
                  <div className="storeinfomodal-addr-group">
                    <div className="storeinfomodal-addr-row">
                      <input
                        type="text"
                        value={storeForm.storeAddr}
                        readOnly
                        className="storeinfomodal-input"
                        placeholder="주소 검색을 눌러주세요"
                      />
                      <button
                        type="button"
                        className="storeinfomodal-addr-search-btn"
                        onClick={() => setAddressSearchOpen(true)}
                      >
                        주소 검색
                      </button>
                    </div>
                    <input
                      type="text"
                      value={storeForm.storeAddrDetail}
                      onChange={(e) =>
                        setStoreForm({
                          ...storeForm,
                          storeAddrDetail: e.target.value,
                        })
                      }
                      className="storeinfomodal-input"
                      placeholder="상세주소"
                    />
                  </div>
                ) : (
                  <span className="storeinfomodal-value">
                    {storeData?.storeAddr} {storeData?.storeAddrDetail}
                  </span>
                )}
              </div>

              <div className="storeinfomodal-field">
                <label className="storeinfomodal-label">연락처</label>
                {editModes.store ? (
                  <input
                    type="text"
                    value={storeForm.storeTelNo}
                    onChange={(e) =>
                      setStoreForm({ ...storeForm, storeTelNo: e.target.value })
                    }
                    className="storeinfomodal-input"
                  />
                ) : (
                  <span className="storeinfomodal-value">
                    {storeData?.storeTelNo}
                  </span>
                )}
              </div>

              <div className="storeinfomodal-field storeinfomodal-field-full">
                <label className="storeinfomodal-label">영업일</label>
                {editModes.store ? (
                  <div className="storeinfomodal-checkbox-group">
                    {openDayOptions.map((day) => (
                      <label
                        key={day.value}
                        className="storeinfomodal-checkbox-label"
                      >
                        <input
                          type="checkbox"
                          checked={storeForm.storeOpenDate.includes(day.value)}
                          onChange={(e) => {
                            if (e.target.checked) {
                              setStoreForm({
                                ...storeForm,
                                storeOpenDate: [
                                  ...storeForm.storeOpenDate,
                                  day.value,
                                ],
                              });
                            } else {
                              setStoreForm({
                                ...storeForm,
                                storeOpenDate: storeForm.storeOpenDate.filter(
                                  (d) => d !== day.value,
                                ),
                              });
                            }
                          }}
                        />
                        <span>{day.label}</span>
                      </label>
                    ))}
                  </div>
                ) : (
                  <span className="storeinfomodal-value">
                    {storeData?.storeOpenDate?.join(", ") || "-"}
                  </span>
                )}
              </div>

              <div className="storeinfomodal-field">
                <label className="storeinfomodal-label">영업 시간</label>
                {editModes.store ? (
                  <div className="storeinfomodal-combined-input">
                    <input
                      type="time"
                      value={storeForm.storeOpenTime}
                      onChange={(e) =>
                        setStoreForm({
                          ...storeForm,
                          storeOpenTime: e.target.value,
                        })
                      }
                      className="storeinfomodal-input"
                    />
                    <span>~</span>
                    <input
                      type="time"
                      value={storeForm.storeCloseTime}
                      onChange={(e) =>
                        setStoreForm({
                          ...storeForm,
                          storeCloseTime: e.target.value,
                        })
                      }
                      className="storeinfomodal-input"
                    />
                  </div>
                ) : (
                  <span className="storeinfomodal-value">
                    {storeData?.storeOpenTime} ~ {storeData?.storeCloseTime}
                  </span>
                )}
              </div>

              <div className="storeinfomodal-field">
                <label className="storeinfomodal-label">은행</label>
                {editModes.store ? (
                  <select
                    value={storeForm.bankIdx}
                    onChange={(e) =>
                      setStoreForm({ ...storeForm, bankIdx: e.target.value })
                    }
                    className="storeinfomodal-select"
                  >
                    <option value="">은행 선택</option>
                    {bankList.map((bank) => (
                      <option key={bank.bankIdx} value={bank.bankIdx}>
                        {bank.bankNm}
                      </option>
                    ))}
                  </select>
                ) : (
                  <span className="storeinfomodal-value">
                    {storeData?.bankNm}
                  </span>
                )}
              </div>

              <div className="storeinfomodal-field">
                <label className="storeinfomodal-label">계좌번호</label>
                {editModes.store ? (
                  <input
                    type="text"
                    value={storeForm.storeAccNo}
                    onChange={(e) =>
                      setStoreForm({ ...storeForm, storeAccNo: e.target.value })
                    }
                    className="storeinfomodal-input"
                  />
                ) : (
                  <span className="storeinfomodal-value">
                    {storeData?.storeAccNo}
                  </span>
                )}
              </div>

              <div className="storeinfomodal-field">
                <label className="storeinfomodal-label">상태</label>
                <div className="storeinfomodal-approval-container">
                  {!isWithdrawal && editModes.store ? (
                    <select
                      className="storeinfomodal-input"
                      value={storeForm.approvalStatus}
                      onChange={(e) =>
                        setStoreForm({
                          ...storeForm,
                          approvalStatus: e.target.value,
                        })
                      }
                    >
                      <option value="승인">승인</option>
                      <option value="제재">제재</option>
                    </select>
                  ) : (
                    <span
                      className={`storeinfomodal-approval-status ${storeData?.approvalStatus === "승인" ? "storeinfomodal-approval-approved" : storeData?.approvalStatus === "제재" ? "storeinfomodal-approval-rejected" : "storeinfomodal-approval-pending"}`}
                    >
                      {storeData?.approvalStatus}
                    </span>
                  )}
                </div>
              </div>
            </div>
          </section>

          {/* 2. 구독 정보 섹션 */}
          <section className="storeinfomodal-section">
            <div className="storeinfomodal-section-header">
              <h3 className="storeinfomodal-section-title">구독 정보</h3>
              {!isWithdrawal &&
                (editModes.subscription ? (
                  <div className="storeinfomodal-section-actions">
                    <button
                      className="storeinfomodal-btn storeinfomodal-save-btn"
                      onClick={handleSaveSubscriptionInfo}
                    >
                      저장
                    </button>
                    <button
                      className="storeinfomodal-btn storeinfomodal-cancel-btn"
                      onClick={() => handleCancel("subscription")}
                    >
                      취소
                    </button>
                  </div>
                ) : (
                  <button
                    className="storeinfomodal-btn storeinfomodal-edit-btn"
                    onClick={() => toggleEditMode("subscription")}
                  >
                    수정
                  </button>
                ))}
            </div>
            <div className="storeinfomodal-grid">
              <div className="storeinfomodal-field">
                <label className="storeinfomodal-label">플랜명</label>
                {editModes.subscription ? (
                  <div className="storeinfomodal-plan-wrapper">
                    <select
                      value={
                        subscriptionForm.membershipPlanIdx?.toString() || ""
                      }
                      onChange={(e) => {
                        setSubscriptionForm({
                          ...subscriptionForm,
                          membershipPlanIdx: e.target.value,
                        });
                      }}
                      className="storeinfomodal-select"
                    >
                      <option value="">플랜 선택</option>
                      {membershipList.map((plan) => (
                        <option
                          key={plan.planIdx}
                          value={plan.planIdx?.toString()}
                        >
                          {plan.planNm}
                        </option>
                      ))}
                    </select>
                  </div>
                ) : (
                  <span className="storeinfomodal-value">
                    {storeData?.membershipNm}
                  </span>
                )}
              </div>

              <div className="storeinfomodal-field">
                <label className="storeinfomodal-label">플랜 가격</label>
                <span className="storeinfomodal-value">
                  {editModes.subscription && subscriptionForm.membershipPlanIdx
                    ? getSelectedPlan().planPrice?.toLocaleString() + "원"
                    : storeData?.membershipPrice?.toLocaleString() + "원"}
                </span>
              </div>

              <div className="storeinfomodal-field">
                <label className="storeinfomodal-label">구독 상태</label>
                {editModes.subscription ? (
                  <select
                    value={subscriptionForm.isRenew}
                    onChange={(e) =>
                      setSubscriptionForm({
                        ...subscriptionForm,
                        isRenew: e.target.value === "true",
                      })
                    }
                    className="storeinfomodal-select"
                  >
                    <option value="true">유지</option>
                    <option value="false">해지</option>
                  </select>
                ) : (
                  <span className="storeinfomodal-value">
                    {storeData?.isRenew ? "유지" : "해지"}
                  </span>
                )}
              </div>
              <div className="storeinfomodal-field">
                <label className="storeinfomodal-label">다음 결제일</label>
                {editModes.subscription ? (
                  <input
                    type="date"
                    value={subscriptionForm.nextBillingDate}
                    onChange={(e) =>
                      setSubscriptionForm({
                        ...subscriptionForm,
                        nextBillingDate: e.target.value,
                      })
                    }
                    className="storeinfomodal-input"
                    disabled
                  />
                ) : (
                  <span className="storeinfomodal-value">
                    {storeData?.nextBillingDate
                      ? new Date(storeData.nextBillingDate).toLocaleDateString()
                      : "-"}
                  </span>
                )}
              </div>
              <div className="storeinfomodal-field">
                <label className="storeinfomodal-label">
                  남은 할인 등록 횟수
                </label>
                {editModes.subscription ? (
                  <span className="storeinfomodal-value">
                    {storeData?.discountRegCount}회
                  </span>
                ) : (
                  <span className="storeinfomodal-value">
                    {storeData?.discountRegCount}회
                  </span>
                )}
              </div>
              <div className="storeinfomodal-field">
                <label className="storeinfomodal-label">
                  남은 네고 등록 횟수
                </label>
                {editModes.subscription ? (
                  <span className="storeinfomodal-value">
                    {storeData?.negoRegCount}회
                  </span>
                ) : (
                  <span className="storeinfomodal-value">
                    {storeData?.negoRegCount}회
                  </span>
                )}
              </div>
              <div className="storeinfomodal-field">
                <label className="storeinfomodal-label">
                  할인/네고 노출 시간
                </label>
                {editModes.subscription ? (
                  <span className="storeinfomodal-value">
                    {storeData?.limitTime}시간
                  </span>
                ) : (
                  <span className="storeinfomodal-value">
                    {storeData?.limitTime}시간
                  </span>
                )}
              </div>
            </div>
          </section>

          {/* 3. 사용자 정보 섹션 */}
          <section className="storeinfomodal-section">
            <div className="storeinfomodal-section-header">
              <h3 className="storeinfomodal-section-title">사용자 정보</h3>
              {!isWithdrawal &&
                (editModes.member ? (
                  <div className="storeinfomodal-section-actions">
                    <button
                      className="storeinfomodal-btn storeinfomodal-save-btn"
                      onClick={handleSaveMemberInfo}
                    >
                      저장
                    </button>
                    <button
                      className="storeinfomodal-btn storeinfomodal-cancel-btn"
                      onClick={() => handleCancel("member")}
                    >
                      취소
                    </button>
                  </div>
                ) : (
                  <button
                    className="storeinfomodal-btn storeinfomodal-edit-btn"
                    onClick={() => toggleEditMode("member")}
                  >
                    수정
                  </button>
                ))}
            </div>
            <div className="storeinfomodal-grid">
              <div className="storeinfomodal-field">
                <label className="storeinfomodal-label">아이디</label>
                {editModes.member ? (
                  <input
                    type="text"
                    value={memberForm.memberLoginId}
                    onChange={(e) =>
                      setMemberForm({
                        ...memberForm,
                        memberLoginId: e.target.value,
                      })
                    }
                    className="storeinfomodal-input"
                  />
                ) : (
                  <span className="storeinfomodal-value">
                    {storeData?.memberLoginId}
                  </span>
                )}
              </div>

              <div className="storeinfomodal-field">
                <label className="storeinfomodal-label">이름</label>
                {editModes.member ? (
                  <input
                    type="text"
                    value={memberForm.ownerNm}
                    onChange={(e) =>
                      setMemberForm({ ...memberForm, ownerNm: e.target.value })
                    }
                    className="storeinfomodal-input"
                    disabled
                  />
                ) : (
                  <span className="storeinfomodal-value">
                    {storeData?.ownerNm}
                  </span>
                )}
              </div>

              <div className="storeinfomodal-field">
                <label className="storeinfomodal-label">연락처</label>
                {editModes.member ? (
                  <input
                    type="text"
                    value={memberForm.ownerTelNo}
                    onChange={(e) =>
                      setMemberForm({
                        ...memberForm,
                        ownerTelNo: e.target.value,
                      })
                    }
                    className="storeinfomodal-input"
                  />
                ) : (
                  <span className="storeinfomodal-value">
                    {storeData?.ownerTelNo}
                  </span>
                )}
              </div>

              {editModes.member && (
                <div className="storeinfomodal-field">
                  <label className="storeinfomodal-label">비밀번호 변경</label>
                  <input
                    type="password"
                    value={memberForm.newPassword}
                    onChange={(e) =>
                      setMemberForm({
                        ...memberForm,
                        newPassword: e.target.value,
                      })
                    }
                    className="storeinfomodal-input"
                    placeholder="새 비밀번호 (변경 시에만 입력)"
                  />
                </div>
              )}
            </div>
          </section>

          {/* 4. 사업자 정보 섹션 */}
          <section className="storeinfomodal-section">
            <div className="storeinfomodal-section-header">
              <h3 className="storeinfomodal-section-title">사업자 정보</h3>
              {!isWithdrawal &&
                (editModes.business ? (
                  <div className="storeinfomodal-section-actions">
                    <button
                      className="storeinfomodal-btn storeinfomodal-save-btn"
                      onClick={handleSaveBusinessInfo}
                    >
                      저장
                    </button>
                    <button
                      className="storeinfomodal-btn storeinfomodal-cancel-btn"
                      onClick={() => handleCancel("business")}
                    >
                      취소
                    </button>
                  </div>
                ) : (
                  <button
                    className="storeinfomodal-btn storeinfomodal-edit-btn"
                    onClick={() => toggleEditMode("business")}
                  >
                    수정
                  </button>
                ))}
            </div>
            <div className="storeinfomodal-grid">
              <div className="storeinfomodal-field">
                <label className="storeinfomodal-label">상호명</label>
                {editModes.business ? (
                  <input
                    type="text"
                    value={businessForm.bizNm}
                    onChange={(e) =>
                      setBusinessForm({
                        ...businessForm,
                        bizNm: e.target.value,
                      })
                    }
                    className="storeinfomodal-input"
                  />
                ) : (
                  <span className="storeinfomodal-value">
                    {storeData?.bizNm}
                  </span>
                )}
              </div>

              <div className="storeinfomodal-field">
                <label className="storeinfomodal-label">업태/업종</label>
                {editModes.business ? (
                  <select
                    value={businessForm.bizType}
                    onChange={(e) =>
                      setBusinessForm({
                        ...businessForm,
                        bizType: e.target.value,
                      })
                    }
                    className="storeinfomodal-select"
                  >
                    <option value="개인">개인</option>
                    <option value="법인">법인</option>
                  </select>
                ) : (
                  <span className="storeinfomodal-value">
                    {storeData?.bizType}
                  </span>
                )}
              </div>

              <div className="storeinfomodal-field">
                <label className="storeinfomodal-label">대표자명</label>
                {editModes.business ? (
                  <span className="storeinfomodal-value">
                    {storeData?.ownerNm}
                  </span>
                ) : (
                  <span className="storeinfomodal-value">
                    {storeData?.ownerNm}
                  </span>
                )}
              </div>

              <div className="storeinfomodal-field">
                <label className="storeinfomodal-label">사업자 등록일</label>
                {editModes.business ? (
                  <input
                    type="date"
                    value={businessForm.bizRegDate}
                    onChange={(e) =>
                      setBusinessForm({
                        ...businessForm,
                        bizRegDate: e.target.value,
                      })
                    }
                    className="storeinfomodal-input"
                  />
                ) : (
                  <span className="storeinfomodal-value">
                    {storeData?.bizRegDate
                      ? new Date(storeData.bizRegDate).toLocaleDateString()
                      : "-"}
                  </span>
                )}
              </div>

              <div className="storeinfomodal-field">
                <label className="storeinfomodal-label">사업자 번호</label>
                {editModes.business ? (
                  <input
                    type="text"
                    value={businessForm.bizNo}
                    onChange={(e) =>
                      setBusinessForm({
                        ...businessForm,
                        bizNo: e.target.value,
                      })
                    }
                    className="storeinfomodal-input"
                  />
                ) : (
                  <span className="storeinfomodal-value">
                    {storeData?.bizNo}
                  </span>
                )}
              </div>

              <div className="storeinfomodal-field">
                <label className="storeinfomodal-label">대표 연락처</label>
                {editModes.business ? (
                  <input
                    type="text"
                    value={businessForm.ownerTelNo}
                    onChange={(e) =>
                      setBusinessForm({
                        ...businessForm,
                        ownerTelNo: e.target.value,
                      })
                    }
                    className="storeinfomodal-input"
                  />
                ) : (
                  <span className="storeinfomodal-value">
                    {storeData?.ownerTelNo}
                  </span>
                )}
              </div>

              <div className="storeinfomodal-field">
                <label className="storeinfomodal-label">대표 이메일</label>
                <span className="storeinfomodal-value">
                  {storeData?.ownerEmail || "-"}
                </span>
              </div>

              <div className="storeinfomodal-field">
                <label className="storeinfomodal-label">승인일시</label>
                <span className="storeinfomodal-value">
                  {storeData?.approvedDateTime
                    ? formatDateTime24(storeData.approvedDateTime)
                    : "-"}
                </span>
              </div>

              <div className="storeinfomodal-field storeinfomodal-field-full">
                <label className="storeinfomodal-label">가맹점 메모</label>
                {editModes.business ? (
                  <textarea
                    value={businessForm.storeNote}
                    onChange={(e) =>
                      setBusinessForm({
                        ...businessForm,
                        storeNote: e.target.value,
                      })
                    }
                    className="storeinfomodal-input"
                    rows={3}
                  />
                ) : (
                  <span className="storeinfomodal-value">
                    {storeData?.storeNote || "-"}
                  </span>
                )}
              </div>
            </div>
          </section>

          {/* 4-1. 파일 정보 섹션 */}
          <section className="storeinfomodal-section">
            <div className="storeinfomodal-section-header">
              <h3 className="storeinfomodal-section-title">파일 정보</h3>
            </div>

            {/* 사업자 등록증 */}
            {storeData?.bzFileMaster?.files &&
              storeData.bzFileMaster.files.length > 0 && (
                <div className="storeinfomodal-files-group">
                  <h4 className="storeinfomodal-files-title">사업자 등록증</h4>
                  <div className="storeinfomodal-files-gallery">
                    {storeData.bzFileMaster.files.map((file, index) => {
                      const fileType = getFileType(file.originalName);
                      return (
                        <div
                          key={index}
                          className="storeinfomodal-file-item"
                          onClick={() => handleFileClick(file)}
                          style={{ cursor: "pointer" }}
                        >
                          {fileType === "pdf" ? (
                            <div className="storeinfomodal-file-pdf-icon">
                              <span className="storeinfomodal-file-pdf-text">
                                PDF
                              </span>
                            </div>
                          ) : (
                            <img
                              src={file.fileUrl}
                              alt={`사업자 등록증 ${index + 1}`}
                              className="storeinfomodal-file-image"
                            />
                          )}
                          <p className="storeinfomodal-file-name">
                            {file.originalName}
                          </p>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

            {/* 가게 이미지 */}
            {storeData?.storeImgFileMaster?.files &&
              storeData.storeImgFileMaster.files.length > 0 && (
                <div className="storeinfomodal-files-group">
                  <h4 className="storeinfomodal-files-title">가게 이미지</h4>
                  <div className="storeinfomodal-files-gallery">
                    {storeData.storeImgFileMaster.files.map((file, index) => {
                      const fileType = getFileType(file.originalName);
                      return (
                        <div
                          key={index}
                          className="storeinfomodal-file-item"
                          onClick={() => handleFileClick(file)}
                          style={{ cursor: "pointer" }}
                        >
                          {fileType === "pdf" ? (
                            <div className="storeinfomodal-file-pdf-icon">
                              <span className="storeinfomodal-file-pdf-text">
                                PDF
                              </span>
                            </div>
                          ) : (
                            <img
                              src={file.fileUrl}
                              alt={`가게 이미지 ${index + 1}`}
                              className="storeinfomodal-file-image"
                            />
                          )}
                          <p className="storeinfomodal-file-name">
                            {file.originalName}
                          </p>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
          </section>

          {/* 5. 권한 정보 섹션 */}
          <section className="storeinfomodal-section">
            <div className="storeinfomodal-section-header">
              <h3 className="storeinfomodal-section-title">권한 정보</h3>
              {!isWithdrawal &&
                (editModes.auth ? (
                  <div className="storeinfomodal-section-actions">
                    <button
                      className="storeinfomodal-btn storeinfomodal-save-btn"
                      onClick={handleSaveAuthInfo}
                    >
                      저장
                    </button>
                    <button
                      className="storeinfomodal-btn storeinfomodal-cancel-btn"
                      onClick={() => handleCancel("auth")}
                    >
                      취소
                    </button>
                  </div>
                ) : (
                  <button
                    className="storeinfomodal-btn storeinfomodal-edit-btn"
                    onClick={() => toggleEditMode("auth")}
                  >
                    수정
                  </button>
                ))}
            </div>

            <div className="storeinfomodal-auth-row">
              <div className="storeinfomodal-auth-field">
                <label className="storeinfomodal-label">권한명</label>
                {editModes.auth ? (
                  <select
                    value={selectedAuthIdx || ""}
                    onChange={(e) => handleAuthChange(e.target.value)}
                    className="storeinfomodal-select"
                  >
                    <option value="">권한 선택</option>
                    {authList.map((auth) => (
                      <option key={auth.authIdx} value={auth.authIdx}>
                        {auth.authNm}
                      </option>
                    ))}
                  </select>
                ) : (
                  <span className="storeinfomodal-value">
                    {storeData?.authNm}
                  </span>
                )}
              </div>

              <div className="storeinfomodal-auth-field">
                <label className="storeinfomodal-label">권한 설명</label>
                {editModes.auth && selectedAuthIdx ? (
                  <span className="storeinfomodal-value storeinfomodal-auth-description">
                    {getSelectedAuth()?.authDescription || "-"}
                  </span>
                ) : (
                  <span className="storeinfomodal-value storeinfomodal-auth-description">
                    {storeData?.authDescription || "-"}
                  </span>
                )}
              </div>
            </div>

            <div className="storeinfomodal-menu-section">
              <h4 className="storeinfomodal-menu-title">메뉴 권한</h4>
              {editModes.auth ? (
                <div className="storeinfomodal-menu-grid">
                  {getAvailableMenus().map((menu) => (
                    <label
                      key={menu.code}
                      className="storeinfomodal-checkbox-label"
                    >
                      <input
                        type="checkbox"
                        checked={selectedMenus.includes(menu.code)}
                        onChange={() => handleMenuToggle(menu.code)}
                      />
                      <span>{menu.displayName}</span>
                    </label>
                  ))}
                </div>
              ) : (
                <div className="storeinfomodal-permission-list">
                  {Array.isArray(storeData?.menuAuth) ? (
                    storeData.menuAuth.map((menu, index) => (
                      <span
                        key={index}
                        className="storeinfomodal-permission-tag"
                      >
                        {menu.displayName}
                      </span>
                    ))
                  ) : (
                    <span className="storeinfomodal-no-permission">
                      접근 가능한 메뉴가 없습니다
                    </span>
                  )}
                </div>
              )}
            </div>
          </section>

          {/* 6. 매니저 정보 섹션 */}
          <section className="storeinfomodal-section">
            <div className="storeinfomodal-section-header">
              <h3 className="storeinfomodal-section-title">매니저 정보</h3>
            </div>

            <div className="storeinfomodal-manager-list">
              {Array.isArray(storeData?.members) &&
              storeData.members.length > 0 ? (
                storeData.members.map((member, index) => (
                  <div
                    key={member.memberIdx}
                    className="storeinfomodal-manager-card"
                  >
                    <div className="storeinfomodal-manager-header">
                      <div className="storeinfomodal-manager-info">
                        <span className="storeinfomodal-manager-name">
                          {member.memberNm}
                        </span>
                        <span className="storeinfomodal-manager-type">
                          {member.memberType}
                        </span>
                      </div>
                      <div className="storeinfomodal-manager-actions">
                        <button
                          className="storeinfomodal-manager-password-btn"
                          onClick={() =>
                            handleManagerPasswordChange(
                              member.memberId,
                              member.memberNm,
                            )
                          }
                        >
                          비밀번호 변경
                        </button>
                        <button
                          className="storeinfomodal-manager-delete-btn"
                          onClick={() =>
                            handleManagerDelete(
                              member.memberId,
                              member.memberNm,
                            )
                          }
                        >
                          삭제
                        </button>
                      </div>
                    </div>

                    <div className="storeinfomodal-manager-details">
                      <div className="storeinfomodal-manager-row">
                        <label>아이디:</label>
                        <span>{member.memberId}</span>
                      </div>
                      <div className="storeinfomodal-manager-row">
                        <label>이메일:</label>
                        <span>{member.memberEmail || "-"}</span>
                      </div>
                      <div className="storeinfomodal-manager-row">
                        <label>연락처:</label>
                        <span>{member.memberTelNo || "-"}</span>
                      </div>
                      <div className="storeinfomodal-manager-row">
                        <label>권한:</label>
                        <div className="storeinfomodal-manager-menus">
                          {Array.isArray(member.menuAuth) &&
                          member.menuAuth.length > 0 ? (
                            member.menuAuth.map((menu, idx) => (
                              <span
                                key={idx}
                                className="storeinfomodal-manager-menu-tag"
                              >
                                {menu.displayName}
                              </span>
                            ))
                          ) : (
                            <span>권한 없음</span>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                ))
              ) : (
                <div className="storeinfomodal-no-manager">
                  등록된 매니저가 없습니다.
                </div>
              )}
            </div>
          </section>
        </div>

        {/* 푸터 */}
        <div className="storeinfomodal-footer">
          <button
            className="storeinfomodal-btn storeinfomodal-close-footer-btn"
            onClick={onClose}
          >
            닫기
          </button>
        </div>
      </div>

      {/* 매니저 비밀번호 변경 모달 */}
      {managerPasswordModal.isOpen && (
        <div
          className="storeinfomodal-password-modal-backdrop"
          onClick={handleManagerPasswordCancel}
        >
          <div
            className="storeinfomodal-password-modal"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="storeinfomodal-password-modal-header">
              <h3 className="storeinfomodal-password-modal-title">
                {managerPasswordModal.memberNm} 매니저 비밀번호 변경
              </h3>
              <button
                className="storeinfomodal-password-modal-close"
                onClick={handleManagerPasswordCancel}
              >
                ✕
              </button>
            </div>

            <div className="storeinfomodal-password-modal-content">
              <div className="storeinfomodal-password-field">
                <label className="storeinfomodal-password-label">
                  새 비밀번호
                </label>
                <input
                  type="password"
                  className="storeinfomodal-password-input"
                  placeholder="새 비밀번호를 입력하세요"
                  value={managerPasswordModal.newPassword}
                  onChange={(e) =>
                    setManagerPasswordModal({
                      ...managerPasswordModal,
                      newPassword: e.target.value,
                    })
                  }
                />
                <div
                  className={`storeinfomodal-password-helper ${
                    managerPasswordModal.newPassword.length >= 8 &&
                    managerPasswordModal.newPassword.length <= 16 &&
                    /[0-9]/.test(managerPasswordModal.newPassword) &&
                    /[a-zA-Z]/.test(managerPasswordModal.newPassword) &&
                    /\W/.test(managerPasswordModal.newPassword)
                      ? "valid"
                      : ""
                  }`}
                >
                  8~16자, 숫자, 영문, 특수문자를 포함해야 합니다.
                </div>
              </div>

              <div className="storeinfomodal-password-field">
                <label className="storeinfomodal-password-label">
                  비밀번호 확인
                </label>
                <input
                  type="password"
                  className="storeinfomodal-password-input"
                  placeholder="비밀번호를 재입력하세요"
                  value={managerPasswordModal.confirmPassword}
                  onChange={(e) =>
                    setManagerPasswordModal({
                      ...managerPasswordModal,
                      confirmPassword: e.target.value,
                    })
                  }
                />
              </div>
            </div>

            <div className="storeinfomodal-password-modal-footer">
              <button
                className="storeinfomodal-password-modal-btn storeinfomodal-password-modal-save"
                onClick={handleManagerPasswordSave}
              >
                변경
              </button>
              <button
                className="storeinfomodal-password-modal-btn storeinfomodal-password-modal-cancel"
                onClick={handleManagerPasswordCancel}
              >
                취소
              </button>
            </div>
          </div>
        </div>
      )}

      {/* 이미지/PDF 패널 */}
      <div
        className={`storeinfomodal-img ${selectedImage.name?.toLowerCase().endsWith(".pdf") ? "storeinfomodal-img-pdf" : ""}`}
        onClick={(e) => e.stopPropagation()}
      >
        {selectedImage.url ? (
          <>
            {selectedImage.name?.toLowerCase().endsWith(".pdf") ? (
              <iframe
                src={selectedImage.url}
                className="storeinfomodal-img-pdf-viewer"
                title={selectedImage.name}
              />
            ) : (
              <img
                src={selectedImage.url}
                alt={selectedImage.name}
                className="storeinfomodal-img-content storeinfomodal-img-clickable"
                onClick={() =>
                  setImageViewer({
                    isOpen: true,
                    imageUrl: selectedImage.url,
                    imageName: selectedImage.name,
                  })
                }
                title="클릭하면 크게 볼 수 있습니다"
              />
            )}
            <div className="storeinfomodal-img-footer">
              <p className="storeinfomodal-img-name">{selectedImage.name}</p>
            </div>
          </>
        ) : (
          <span className="storeinfomodal-img-placeholder">
            이미지를 선택하세요
          </span>
        )}
      </div>

      {/* 주소 검색 모달 */}
      {addressSearchOpen &&
        createPortal(
          <div
            className="storeinfomodal-addr-modal-overlay"
            onClick={() => setAddressSearchOpen(false)}
          >
            <div
              className="storeinfomodal-addr-modal-content"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="storeinfomodal-addr-modal-header">
                <h3>주소 검색</h3>
                <button
                  className="storeinfomodal-addr-modal-close"
                  onClick={() => setAddressSearchOpen(false)}
                >
                  ×
                </button>
              </div>
              <DaumPostcode
                onComplete={handleAddressComplete}
                autoClose={false}
                style={{ height: "550px" }}
              />
            </div>
          </div>,
          document.body,
        )}

      {/* 이미지 확대 뷰어 */}
      {imageViewer.isOpen &&
        createPortal(
          <div
            className="storeinfomodal-lightbox-overlay"
            onClick={() =>
              setImageViewer({ isOpen: false, imageUrl: "", imageName: "" })
            }
          >
            <div
              className="storeinfomodal-lightbox-content"
              onClick={(e) => e.stopPropagation()}
            >
              <button
                className="storeinfomodal-lightbox-close"
                onClick={() =>
                  setImageViewer({ isOpen: false, imageUrl: "", imageName: "" })
                }
              >
                ×
              </button>
              <img
                src={imageViewer.imageUrl}
                alt={imageViewer.imageName}
                className="storeinfomodal-lightbox-img"
              />
              <p className="storeinfomodal-lightbox-name">
                {imageViewer.imageName}
              </p>
            </div>
          </div>,
          document.body,
        )}
    </div>
  );
};

export default StoreInfoModal;
